import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import debounce from "debounce-promise";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { formatPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";

import CircleButton from "@components/buttons/CircleButton";
import ClientAvatar from "@components/ClientAvatar";
import ConditionalTooltip from "@components/ConditionalTooltip";
import BaseErrors from "@components/fields/BaseErrors";
import BooleanRadioButtonGroup from "@components/fields/BooleanRadioButtonGroup";
import CareProfileInput from "@components/fields/CareProfileInput";
import LocationAutocompleteInput from "@components/fields/LocationAutocompleteInput";
import PhoneInput from "@components/fields/PhoneInput";
import Select from "@components/fields/Select";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import CareProfileFormCondition from "@components/forms/careProfile/CareProfileFormCondition";
import Form from "@components/forms/Form";
import BodyCopy from "@components/typography/BodyCopy";
import useCopyToClipboard from "@hooks/useCopyToClipboard";
import {
  CaregivingDurationEnum,
  CaregivingImpactEnum,
  CaregivingTimePerWeekEnum,
  CareProfileFieldNameEnum,
  CareProfileFieldScopeEnum,
  CareProfileUseCaseEnum,
  CareTypeEnum,
  ClientRoleProviderEmotionalStateEnum,
  ClientRoleProviderFeelsSupportedEnum,
  ClientRoleRelationshipEnum,
  ClientRoleUpdateDocument,
  ClientRoleUpdateMutation,
  ClientRoleUpdateMutationVariables,
  ClientUpdateDocument,
  ClientUpdateMutation,
  ClientUpdateMutationVariables,
  DistanceToLovedOneEnum,
  GetCareProfileFieldRulesQuery,
  GetJourneyWithCareProfileQuery,
  PronounsEnum,
  YesNoDontKnowEnum
} from "@typing/Generated";
import { filterForDirtyValidValues, handleMutationResponse, ThingWithErrors } from "@utils/formUtils";
import { cityOrLowerLocationTypes } from "@utils/locationUtils";
import { getCareProfileName } from "@utils/modelUtils";
import {
  optionsForCaregivingDuration,
  optionsForCaregivingImpact,
  optionsForCaregivingTimePerWeek,
  optionsForCaregivingTimePerWeekGrieving,
  optionsForCareTypes,
  optionsForDistanceToLovedOne,
  optionsForIsVeteran,
  optionsForPronouns,
  optionsForProviderEmotionalState,
  optionsForProviderFeelsSupported,
  optionsForRelationship,
  optionsForYesNoDontKnow
} from "@utils/optionUtils";
import { isValidPhoneNumber } from "@utils/stringUtils";

type CareProfileFieldRules = GetCareProfileFieldRulesQuery["careProfileFieldRules"];

type Props = {
  careProfileFieldRules: CareProfileFieldRules;
  clientRoleId: string | null | undefined;
  journey: GetJourneyWithCareProfileQuery["journey"];
};

type FormData = {
  careTypesProvided?: CareTypeEnum[];
  caregivingDuration?: CaregivingDurationEnum | null;
  caregivingImpactFinancial?: CaregivingImpactEnum | null;
  caregivingImpactPhysical?: CaregivingImpactEnum | null;
  caregivingImpactWork?: CaregivingImpactEnum | null;
  caregivingTimePerWeek?: CaregivingTimePerWeekEnum | null;
  culturalConsiderations?: string | null;
  distanceToLovedOne?: DistanceToLovedOneEnum | null;
  email?: string | null;
  everBeenCaregiver?: YesNoDontKnowEnum | null;
  firstName?: string | null;
  isVeteran?: boolean | null;
  lastName?: string | null;
  number?: string | null;
  placeId?: string | null;
  pronouns?: PronounsEnum | null;
  pronounsOther?: string | null;
  providerEmotionalState?: ClientRoleProviderEmotionalStateEnum | null;
  providerFeelsSupported?: ClientRoleProviderFeelsSupportedEnum | null;
  relationship?: ClientRoleRelationshipEnum | null;
  relationshipOther?: string | null;
  supportingAdultChildren?: number | null;
  supportingGrandparents?: number | null;
  supportingMinorChildren?: number | null;
  supportingOther?: number | null;
  supportingParents?: number | null;
};

const CareProfileMemberForm = ({ careProfileFieldRules, clientRoleId, journey }: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const careProfile = journey.careProfile;

  const [updateClient] = useMutation<ClientUpdateMutation, ClientUpdateMutationVariables>(ClientUpdateDocument);

  const [updateClientRole] = useMutation<ClientRoleUpdateMutation, ClientRoleUpdateMutationVariables>(
    ClientRoleUpdateDocument
  );

  const clientRole = useMemo(
    () => journey.clientRoles.find(role => role.id === clientRoleId) ?? journey.clientRoles[0],
    [clientRoleId, journey.clientRoles]
  );

  const calculateDefaultValues = useCallback(
    () => ({
      careTypesProvided: clientRole.careTypesProvided ?? [],
      caregivingDuration: clientRole.caregivingDuration ?? null,
      caregivingImpactFinancial: clientRole.client.caregivingImpactFinancial ?? null,
      caregivingImpactPhysical: clientRole.client.caregivingImpactPhysical ?? null,
      caregivingImpactWork: clientRole.client.caregivingImpactWork ?? null,
      caregivingTimePerWeek: clientRole.client.caregivingTimePerWeek,
      culturalConsiderations: clientRole.client.culturalConsiderations ?? null,
      distanceToLovedOne: clientRole.distanceToLovedOne ?? null,
      email: clientRole.client.email.email ?? null,
      everBeenCaregiver: clientRole.client.everBeenCaregiver ?? null,
      firstName: clientRole.client.firstName ?? null,
      isVeteran: clientRole.client.isVeteran ?? null,
      lastName: clientRole.client.lastName ?? null,
      number: clientRole.client.consultPhoneNumber?.number ?? null,
      placeId: import.meta.env.VITE_NO_GOOGLE_LOCATION_CALLS
        ? "ChIJfX6-HwSbf4gR4Aa9pkeWBLY"
        : clientRole.client.location.placeId,
      pronouns: clientRole.client.pronouns,
      pronounsOther: clientRole.client.pronounsOther,
      providerEmotionalState: clientRole.providerEmotionalState ?? null,
      providerFeelsSupported: clientRole.providerFeelsSupported ?? null,
      relationship: clientRole?.relationship?.relationshipOption ?? null,
      relationshipOther: clientRole?.relationship?.other ?? null,
      supportingAdultChildren: clientRole.client.supportingAdultChildren ?? null,
      supportingGrandparents: clientRole.client.supportingGrandparents ?? null,
      supportingMinorChildren: clientRole.client.supportingMinorChildren ?? null,
      supportingOther: clientRole.client.supportingOther ?? null,
      supportingParents: clientRole.client.supportingParents ?? null
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientRoleId]
  );

  const defaultValues = calculateDefaultValues();

  const clientFields = useMemo(
    () => [
      "caregivingImpactFinancial",
      "caregivingImpactPhysical",
      "caregivingImpactWork",
      "caregivingTimePerWeek",
      "culturalConsiderations",
      "email",
      "everBeenCaregiver",
      "firstName",
      "isVeteran",
      "lastName",
      "number",
      "placeId",
      "pronouns",
      "pronounsOther",
      "supportingAdultChildren",
      "supportingGrandparents",
      "supportingMinorChildren",
      "supportingOther",
      "supportingParents"
    ],
    []
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      careTypesProvided: yup.array().of(yup.string().oneOf(Object.values(CareTypeEnum)).defined()),
      caregivingDuration: yup.string().oneOf(Object.values(CaregivingDurationEnum)).nullable(),
      caregivingImpactFinancial: yup.string().oneOf(Object.values(CaregivingImpactEnum)).nullable(),
      caregivingImpactPhysical: yup.string().oneOf(Object.values(CaregivingImpactEnum)).nullable(),
      caregivingImpactWork: yup.string().oneOf(Object.values(CaregivingImpactEnum)).nullable(),
      caregivingTimePerWeek: yup.string().oneOf(Object.values(CaregivingTimePerWeekEnum)).nullable(),
      culturalConsiderations: yup.string().nullable(),
      distanceToLovedOne: yup.string().oneOf(Object.values(DistanceToLovedOneEnum)).nullable(),
      email: yup
        .string()
        .nullable()
        .email(intl.formatMessage({ id: "errors.careProfile.email.invalid" })),
      everBeenCaregiver: yup.string().oneOf(Object.values(YesNoDontKnowEnum)).nullable(),
      firstName: yup.string().nullable(),
      isVeteran: yup.boolean().nullable(),
      lastName: yup.string().nullable(),
      number: yup
        .string()
        .nullable()
        .test(
          "is-valid-phone",
          intl.formatMessage({ id: "errors.careProfile.number.invalid" }),
          value => !value || isValidPhoneNumber(value)
        ),
      placeId: yup.string().nullable(),
      pronouns: yup.string().oneOf(Object.values(PronounsEnum)).nullable().defined(),
      pronounsOther: yup.string().nullable(),
      providerEmotionalState: yup.string().oneOf(Object.values(ClientRoleProviderEmotionalStateEnum)).nullable(),
      providerFeelsSupported: yup.string().oneOf(Object.values(ClientRoleProviderFeelsSupportedEnum)).nullable(),
      relationship: yup.string().oneOf(Object.values(ClientRoleRelationshipEnum)).nullable(),
      relationshipOther: yup.string().nullable(),
      supportingAdultChildren: yup
        .number()
        .nullable()
        .integer(intl.formatMessage({ id: "errors.client.supportingAdultChildren.integer" }))
        .min(0, intl.formatMessage({ id: "errors.client.supportingAdultChildren.greaterThanOrEqualTo" }))
        .typeError(intl.formatMessage({ id: "errors.client.supportingAdultChildren.number" })),
      supportingGrandparents: yup
        .number()
        .nullable()
        .integer(intl.formatMessage({ id: "errors.client.supportingGrandparents.integer" }))
        .min(0, intl.formatMessage({ id: "errors.client.supportingGrandparents.greaterThanOrEqualTo" }))
        .typeError(intl.formatMessage({ id: "errors.client.supportingGrandparents.number" })),
      supportingMinorChildren: yup
        .number()
        .nullable()
        .integer(intl.formatMessage({ id: "errors.client.supportingMinorChildren.integer" }))
        .min(0, intl.formatMessage({ id: "errors.client.supportingMinorChildren.greaterThanOrEqualTo" }))
        .typeError(intl.formatMessage({ id: "errors.client.supportingMinorChildren.number" })),
      supportingOther: yup
        .number()
        .nullable()
        .integer(intl.formatMessage({ id: "errors.client.supportingOther.integer" }))
        .min(0, intl.formatMessage({ id: "errors.client.supportingOther.greaterThanOrEqualTo" }))
        .typeError(intl.formatMessage({ id: "errors.client.supportingOther.number" })),
      supportingParents: yup
        .number()
        .nullable()
        .integer(intl.formatMessage({ id: "errors.client.supportingParents.integer" }))
        .min(0, intl.formatMessage({ id: "errors.client.supportingParents.greaterThanOrEqualTo" }))
        .typeError(intl.formatMessage({ id: "errors.client.supportingParents.number" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    form.reset(calculateDefaultValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateDefaultValues]);

  const submitClient = useCallback(
    async (clientId: string) => {
      const dirtyValidClientUpdateVariables = filterForDirtyValidValues(form);
      const fieldsToBeSubmitted = Object.keys(dirtyValidClientUpdateVariables);
      const clientUpdateVariables: ClientUpdateMutationVariables = {
        id: clientId,
        ...dirtyValidClientUpdateVariables
      };

      if (fieldsToBeSubmitted.includes("number")) {
        clientUpdateVariables.formattedNumber = clientUpdateVariables.number
          ? formatPhoneNumber(clientUpdateVariables.number)
          : undefined;
      }

      if (Object.keys(clientUpdateVariables).length > 0) {
        setBaseErrors([]);
        return updateClient({ variables: clientUpdateVariables }).then(response => {
          const data = [response.data?.clientUpdate] as ThingWithErrors[];
          handleMutationResponse<FormData>({
            data,
            intl,
            onError: () => {},
            onSuccess: () => {},
            setBaseErrors,
            setError: form.setError,
            txBase: "errors.client"
          });
        });
      }
    },
    [form, intl, updateClient]
  );

  const submitClientRole = useCallback(
    async (clientRoleId: string) => {
      const dirtyValidValues = filterForDirtyValidValues(form);
      const fieldsToBeSubmitted = Object.keys(dirtyValidValues);
      const { relationship, relationshipOther, ...dirtyValidClientRoleUpdateVariables } = dirtyValidValues;
      const clientRoleUpdateVariables: ClientRoleUpdateMutationVariables = {
        id: clientRoleId,
        ...dirtyValidClientRoleUpdateVariables
      };

      if (fieldsToBeSubmitted.includes("relationship")) {
        clientRoleUpdateVariables.relationship = {
          other: relationship === ClientRoleRelationshipEnum.OTHER ? relationshipOther : null,
          relationshipOption: relationship
        };
      }

      if (Object.keys(clientRoleUpdateVariables).length > 0) {
        setBaseErrors([]);
        return updateClientRole({ variables: clientRoleUpdateVariables }).then(response => {
          const data = [response.data?.clientRoleUpdate] as ThingWithErrors[];
          handleMutationResponse<FormData>({
            data,
            intl,
            onError: () => {},
            onSuccess: () => {},
            setBaseErrors,
            setError: form.setError,
            txBase: "errors.journey"
          });
        });
      }
    },
    [form, intl, updateClientRole]
  );

  useEffect(() => {
    const debouncedSubmitClient = debounce(submitClient, 2000, { accumulate: false, leading: false });
    const debouncedSubmitClientRole = debounce(submitClientRole, 2000, { accumulate: false, leading: false });

    const subscription = form.watch((_, { name }) => {
      if (clientFields.includes(name ?? "")) {
        const submitFunction = name === "placeId" ? submitClient : debouncedSubmitClient;
        form.handleSubmit(
          () => submitFunction(clientRole.client.id),
          () => submitFunction(clientRole.client.id)
        )();
      } else {
        form.handleSubmit(
          () => debouncedSubmitClientRole(clientRole.id),
          () => debouncedSubmitClientRole(clientRole.id)
        )();
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [clientFields, clientRole.client.id, clientRole.id, form, submitClient, submitClientRole]);

  const caregivingDurationOptions = optionsForCaregivingDuration(intl);
  const caregivingImpactOptions = optionsForCaregivingImpact(intl);
  const caregivingTimePerWeekOptions =
    journey.careProfile.useCase === CareProfileUseCaseEnum.GRIEVING
      ? optionsForCaregivingTimePerWeekGrieving(intl)
      : optionsForCaregivingTimePerWeek(intl);
  const careTypeOptions = optionsForCareTypes(intl);
  const distanceToLovedOneOptions = optionsForDistanceToLovedOne(intl);
  const everBeenCaregiverOptions = optionsForYesNoDontKnow(intl);
  const isVeteranOptions = optionsForIsVeteran(intl);
  const pronounsOptions = optionsForPronouns(intl);
  const providerEmotionalStateOptions = optionsForProviderEmotionalState(intl);
  const providerFeelsSupportedOptions = optionsForProviderFeelsSupported(intl);
  const relationshipOptions = optionsForRelationship(intl, "models.clientRole.relationshipInverse", false, false, {
    name: getCareProfileName({ intl, journey })
  });

  const relationship = useMemo(() => {
    if (clientRole.relationship?.other || clientRole.relationship?.relationshipOption) {
      if (clientRole.relationship?.relationshipOption == ClientRoleRelationshipEnum.OTHER) {
        return clientRole.relationship.other;
      } else {
        return (
          <FormattedMessage
            id={`models.clientRole.relationshipInverse.${clientRole.relationship.relationshipOption}`}
            values={{
              name: getCareProfileName({ intl, journey })
            }}
          />
        );
      }
    }
    return <FormattedMessage id="dictionary.unknown" />;
  }, [clientRole.relationship, intl, journey]);

  const copyToClipboard = useCopyToClipboard(clientRole.client.location.address);

  const placeId = form.watch("placeId");

  return (
    <Form hookForm={form}>
      <BaseErrors errors={baseErrors} />

      <div className={`${styles.careTeamMemberRow}`}>
        <div className={styles.formControlsColumn}>
          <div className={styles.avatarAndName}>
            <ClientAvatar
              bold
              clientId={clientRole.client.id}
              color="secondary-500"
              firstName={clientRole.client.firstName}
              journeyId={clientRole.journey.id}
            />
            <BodyCopy bold>{clientRole.client.fullName}</BodyCopy>
          </div>
        </div>
        <div className={styles.formControlsColumn}>{relationship}</div>
        <div className={styles.formControlsColumn}>{clientRole.client.email.email}</div>
        <div className={styles.formControlsColumn}>{clientRole.client.consultPhoneNumber?.formattedNumber}</div>
      </div>

      <div className={styles.formSectionRow}>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.FIRST_NAME}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="firstName"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.firstName}
              label={intl.formatMessage({ id: "forms.careProfileNew.firstNameLabel" })}
              register={form.register("firstName")}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.LAST_NAME}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="lastName"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.lastName}
              label={intl.formatMessage({ id: "forms.careProfileNew.lastNameLabel" })}
              register={form.register("lastName")}
            />
          </CareProfileFormCondition>

          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.PRONOUNS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="pronouns"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.pronouns}
              label={intl.formatMessage({ id: "forms.careProfileNew.pronounsLabel" })}
              name="pronouns"
              options={pronounsOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.PRONOUNS_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="pronounsOther"
            otherField="pronouns"
            otherValue={PronounsEnum.OTHER}
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.pronounsOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.pronounsOtherLabel" })}
              register={form.register("pronounsOther")}
            />
          </CareProfileFormCondition>
          <CareProfileInput<FormData>
            error={form.formState.errors.email}
            label={intl.formatMessage({ id: "forms.careProfileNew.emailLabel" })}
            register={form.register("email")}
          />
          <PhoneInput<FormData>
            control={form.control}
            error={form.formState.errors.number}
            label={intl.formatMessage({ id: "forms.careProfileNew.numberLabel" })}
            name="number"
            simpleInput
          />
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.RELATIONSHIP}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="relationship"
            scope={CareProfileFieldScopeEnum.PER_CLIENT_ROLE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.relationship}
              label={intl.formatMessage({ id: "forms.careProfileNew.relationshipLabel" })}
              name="relationship"
              options={relationshipOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.RELATIONSHIP_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="relationshipOther"
            otherField="relationship"
            otherValue={ClientRoleRelationshipEnum.OTHER}
            scope={CareProfileFieldScopeEnum.PER_CLIENT_ROLE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.relationshipOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.relationshipOtherLabel" })}
              register={form.register("relationshipOther")}
            />
          </CareProfileFormCondition>
          <div className={styles.selectWithCopyButton}>
            <CareProfileFormCondition<FormData>
              careProfile={careProfile}
              fieldName={CareProfileFieldNameEnum.PLACE_ID}
              fieldRules={careProfileFieldRules}
              form={form}
              formField="placeId"
              scope={CareProfileFieldScopeEnum.PER_CLIENT}
            >
              <LocationAutocompleteInput
                allowedTypes={cityOrLowerLocationTypes}
                control={form.control}
                error={form.formState.errors.placeId}
                label={intl.formatMessage({ id: "forms.careProfileNew.memberPlaceIdLabel" })}
                // It's a bit weird for the location data to come from different places, but by
                // using the placeId from the form.watch, the input will re-render on changes to
                // that value, whereas it otherwise wouldn't necessarily. This matters particularly
                // in the context of switching between the forms for different participants on the
                // journey.
                location={{
                  address: clientRole.client.location.address,
                  placeId: placeId ?? null
                }}
                name="placeId"
              />
            </CareProfileFormCondition>
            {clientRole.client.location.address && (
              <div className={styles.copyButton}>
                <ConditionalTooltip
                  condition={copyToClipboard.showTooltip}
                  content={intl.formatMessage({ id: "dictionary.copied" })}
                >
                  {tooltipTriggerProps => (
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.copy"
                      fill="clear"
                      icon="clone"
                      onClick={copyToClipboard.handleCopyToClipboard}
                      {...tooltipTriggerProps}
                    />
                  )}
                </ConditionalTooltip>
              </div>
            )}
          </div>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.PROVIDER_EMOTIONAL_STATE}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="providerEmotionalState"
            scope={CareProfileFieldScopeEnum.PER_CLIENT_ROLE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.providerEmotionalState}
              label={intl.formatMessage({ id: "forms.careProfileNew.providerEmotionalStateLabel" })}
              name="providerEmotionalState"
              options={providerEmotionalStateOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.PROVIDER_FEELS_SUPPORTED}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="providerFeelsSupported"
            scope={CareProfileFieldScopeEnum.PER_CLIENT_ROLE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.providerFeelsSupported}
              label={intl.formatMessage({ id: "forms.careProfileNew.providerFeelsSupportedLabel" })}
              name="providerFeelsSupported"
              options={providerFeelsSupportedOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.DISTANCE_TO_LOVED_ONE}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="distanceToLovedOne"
            scope={CareProfileFieldScopeEnum.PER_CLIENT_ROLE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.distanceToLovedOne}
              label={intl.formatMessage({ id: "forms.careProfileNew.distanceToLovedOneLabel" })}
              name="distanceToLovedOne"
              options={distanceToLovedOneOptions}
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.IS_VETERAN}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="isVeteran"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <BooleanRadioButtonGroup
              allowDeselection
              control={form.control}
              error={form.formState.errors.isVeteran}
              label={intl.formatMessage({ id: "forms.careProfileNew.isVeteranMemberLabel" })}
              name="isVeteran"
              options={isVeteranOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.SUPPORTING_ADULT_CHILDREN}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="supportingAdultChildren"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <CareProfileInput<FormData>
              emptyValue={null}
              error={form.formState.errors.supportingAdultChildren}
              label={intl.formatMessage({ id: "forms.careProfileNew.supportingAdultChildrenLabel" })}
              min={0}
              register={form.register("supportingAdultChildren")}
              step="1"
              type="number"
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.SUPPORTING_MINOR_CHILDREN}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="supportingMinorChildren"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <CareProfileInput<FormData>
              emptyValue={null}
              error={form.formState.errors.supportingMinorChildren}
              label={intl.formatMessage({ id: "forms.careProfileNew.supportingMinorChildrenLabel" })}
              min={0}
              register={form.register("supportingMinorChildren")}
              step="1"
              type="number"
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.SUPPORTING_PARENTS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="supportingParents"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <CareProfileInput<FormData>
              emptyValue={null}
              error={form.formState.errors.supportingParents}
              label={intl.formatMessage({ id: "forms.careProfileNew.supportingParentsLabel" })}
              min={0}
              register={form.register("supportingParents")}
              step="1"
              type="number"
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.SUPPORTING_GRANDPARENTS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="supportingGrandparents"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <CareProfileInput<FormData>
              emptyValue={null}
              error={form.formState.errors.supportingGrandparents}
              label={intl.formatMessage({ id: "forms.careProfileNew.supportingGrandparentsLabel" })}
              min={0}
              register={form.register("supportingGrandparents")}
              step="1"
              type="number"
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.SUPPORTING_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="supportingOther"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <CareProfileInput<FormData>
              emptyValue={null}
              error={form.formState.errors.supportingOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.supportingOtherLabel" })}
              min={0}
              register={form.register("supportingOther")}
              step="1"
              type="number"
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CAREGIVING_IMPACT_FINANCIAL}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="caregivingImpactFinancial"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.caregivingImpactFinancial}
              label={intl.formatMessage({ id: "forms.careProfileNew.caregivingImpactFinancialLabel" })}
              name="caregivingImpactFinancial"
              options={caregivingImpactOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CAREGIVING_IMPACT_PHYSICAL}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="caregivingImpactPhysical"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.caregivingImpactPhysical}
              label={intl.formatMessage({ id: "forms.careProfileNew.caregivingImpactPhysicalLabel" })}
              name="caregivingImpactPhysical"
              options={caregivingImpactOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CAREGIVING_IMPACT_WORK}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="caregivingImpactWork"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.caregivingImpactWork}
              label={intl.formatMessage({ id: "forms.careProfileNew.caregivingImpactWorkLabel" })}
              name="caregivingImpactWork"
              options={caregivingImpactOptions}
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CULTURAL_CONSIDERATIONS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="culturalConsiderations"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.culturalConsiderations}
              label={intl.formatMessage({ id: "forms.careProfileNew.culturalConsiderationsLabel" })}
              register={form.register("culturalConsiderations")}
              spellcheck
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CAREGIVING_TIME_PER_WEEK}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="caregivingTimePerWeek"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.caregivingTimePerWeek}
              label={intl.formatMessage({ id: "forms.careProfileNew.caregivingTimePerWeekLabel" })}
              name="caregivingTimePerWeek"
              options={caregivingTimePerWeekOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CARE_TYPES_PROVIDED}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="careTypesProvided"
            scope={CareProfileFieldScopeEnum.PER_CLIENT_ROLE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.careTypesProvided}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.careTypesProvidedLabel" })}
              name="careTypesProvided"
              options={careTypeOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.CAREGIVING_DURATION}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="caregivingDuration"
            scope={CareProfileFieldScopeEnum.PER_CLIENT_ROLE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.caregivingDuration}
              label={intl.formatMessage({ id: "forms.careProfileNew.caregivingDurationLabel" })}
              name="caregivingDuration"
              options={caregivingDurationOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.EVER_BEEN_CAREGIVER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="everBeenCaregiver"
            scope={CareProfileFieldScopeEnum.PER_CLIENT}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.everBeenCaregiver}
              label={intl.formatMessage({ id: "forms.careProfileNew.everBeenCaregiverLabel" })}
              name="everBeenCaregiver"
              options={everBeenCaregiverOptions}
            />
          </CareProfileFormCondition>
        </div>
      </div>
    </Form>
  );
};

export default CareProfileMemberForm;
